import React, { useState, useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  addMonths,
  subMonths,
  isSameMonth,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { DroppableCalendarCell } from './DroppableCalendarCell';
import { COLORS } from '@/data';
import type { ScheduledActivity, Clip } from '@/types';

interface DragDropCalendarViewProps {
  scheduledActivities: ScheduledActivity[];
  clips: Clip[];
  onActivityClick?: (activity: ScheduledActivity) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const DragDropCalendarView: React.FC<DragDropCalendarViewProps> = ({
  scheduledActivities,
  clips,
  onActivityClick,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  return (
    <div className="bg-zinc-800/50 rounded-2xl border border-zinc-700/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-700/50">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5" style={{ color: COLORS.lime }} />
          <h2 className="text-lg font-semibold text-white">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 rounded-lg bg-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-600 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCurrentMonth(new Date())}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
          >
            Today
          </button>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 rounded-lg bg-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-600 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-zinc-700/50">
        {WEEKDAYS.map(day => (
          <div
            key={day}
            className="px-2 py-2 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px bg-zinc-700/30 p-1">
        {calendarDays.map(date => (
          <DroppableCalendarCell
            key={format(date, 'yyyy-MM-dd')}
            date={date}
            activities={scheduledActivities}
            clips={clips}
            isCurrentMonth={isSameMonth(date, currentMonth)}
            onActivityClick={onActivityClick}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-zinc-700/50">
        <div className="flex items-center justify-center gap-6 text-xs text-zinc-500">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-4 h-4 rounded border-2"
              style={{ borderColor: COLORS.lime }}
            />
            <span>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
};
